import { useCallback, useState } from 'react'

import { PageState } from '../app/PublicLayout.jsx'
import { assetName, assetUrl, thumbUrl, useCollection } from '../app/useSiteData.js'
import { formatDate } from '../lib/format.js'
import { useI18n } from '../lib/i18n.jsx'
import {
  Badge,
  Card,
  Container,
  EmptyState,
  Grid,
  Media,
  Modal,
  PageHead,
  RowItem,
  RowList,
  Section,
  SectionHead,
} from '../ui/index.js'

/**
 * Patents, software copyrights and competition awards.
 *
 * Two collections, two shapes, one page — because a visitor thinks of them as
 * one thing ("what has this group produced?") while they read completely
 * differently. A patent is a record: title, kind, number, date, all of which
 * want to line up in columns so twenty of them are comparable at a glance. An
 * award is an artefact: the certificate is the evidence, so it gets a card with
 * a real image.
 *
 * Certificates are stored as `{original, png}` pairs. The PNG is always what is
 * displayed — a browser that will not inline a PDF would otherwise show a grey
 * plugin box where the evidence should be — while the original stays one click
 * away, because a scanned certificate the group can no longer download is a
 * certificate the group has lost.
 *
 * 专利、软件著作权与竞赛获奖。
 *
 * 两个集合、两种形状、一个页面——因为访客把它们当成一件事（「这个组做出了什么」），
 * 而它们的阅读方式完全不同。专利是一条记录：名称、类型、编号、日期，都希望排成列，
 * 二十条才能一眼比较。获奖是一件实物：证书就是证据，因此配一张带真实图片的卡片。
 *
 * 证书存成 {original, png} 对。展示的永远是 PNG——否则在不内联渲染 PDF 的浏览器里，
 * 本该是证据的位置会变成一个灰色插件框——原件则始终只差一次点击，因为课题组下载不到
 * 的扫描件，等于课题组已经弄丢的证书。
 */

/** UI string key per patent kind. 专利类型对应的文案 key。 */
const KIND_LABEL_KEY = {
  invention: 'achievements.kindInvention',
  utility: 'achievements.kindUtility',
  software: 'achievements.kindSoftware',
}

/** UI string key per patent status. 专利状态对应的文案 key。 */
const PATENT_STATUS_LABEL_KEY = {
  granted: 'achievements.granted',
  pending: 'achievements.pending',
}

/** UI string key per award level. 获奖级别对应的文案 key。 */
const LEVEL_LABEL_KEY = {
  international: 'achievements.levelInternational',
  national: 'achievements.levelNational',
  provincial: 'achievements.levelProvincial',
  school: 'achievements.levelSchool',
}

/**
 * First displayable certificate on a record.
 *
 * A certificate uploaded as a PDF whose conversion failed has `png: null`; that
 * record still holds a downloadable original, so it is worth returning rather
 * than treating as absent.
 *
 * 记录上第一张可展示的证书。以 PDF 上传且转换失败的证书 png 为 null；这条记录仍然
 * 有可下载的原件，因此值得返回，而不是当作不存在。
 *
 * @param {object} record Patent or award record / 专利或获奖记录。
 * @returns {{original: object, png: object|null}|null} Certificate pair / 证书对。
 */
function firstCertificate(record) {
  const certificates = Array.isArray(record.certificates) ? record.certificates : []
  return certificates.find((certificate) => certificate && (certificate.png || certificate.original)) || null
}

/**
 * Achievements page.
 *
 * 成果页。
 *
 * @returns {JSX.Element} Page / 页面。
 */
export default function Achievements() {
  const { t, pick, lang } = useI18n()
  const patents = useCollection('patents')
  const awards = useCollection('awards')
  const [preview, setPreview] = useState(null)

  const patentItems = patents.data || []
  const awardItems = awards.data || []

  // One retry button, two collections: whichever failed, both are re-requested
  // so the page never ends up half-fresh and half-stale.
  // 一个重试按钮对应两个集合：无论哪个失败都一起重新请求，页面不会一半新一半旧。
  const reloadPatents = patents.reload
  const reloadAwards = awards.reload
  const reload = useCallback(() => {
    reloadPatents()
    reloadAwards()
  }, [reloadPatents, reloadAwards])

  const closePreview = useCallback(() => setPreview(null), [])

  return (
    <Section className="achievements-page">
      <Container width="narrow">
        <PageHead title={t('achievements.title')} lede={t('achievements.lede')} />

        <PageState
          loading={patents.loading || awards.loading}
          error={patents.error || awards.error}
          onRetry={reload}
          isEmpty={patentItems.length === 0 && awardItems.length === 0}
          empty={<EmptyState title={t('achievements.empty')} body={t('achievements.emptyBody')} />}
        >
          {patentItems.length > 0 && (
            <section className="achievements-section">
              <SectionHead
                title={t('achievements.patents')}
                aside={<span className="achievements-section__count">{t('common.count', { n: patentItems.length })}</span>}
              />
              <RowList className="patent-list">
                {patentItems.map((record) => {
                  const certificate = firstCertificate(record)
                  const iso = String(record.date || '').slice(0, 10)

                  return (
                    <RowItem
                      className="patent-row"
                      key={record.id}
                      meta={
                        <time className="patent-row__date" dateTime={iso} title={formatDate(record.date, lang)}>
                          {iso}
                        </time>
                      }
                    >
                      <h3 className="patent-row__title">{pick(record.title)}</h3>
                      <p className="patent-row__meta">
                        {KIND_LABEL_KEY[record.kind] && (
                          <Badge className="patent-row__kind">{t(KIND_LABEL_KEY[record.kind])}</Badge>
                        )}
                        {record.number && <span className="patent-row__number">{record.number}</span>}
                        {PATENT_STATUS_LABEL_KEY[record.patentStatus] && (
                          <span className="patent-row__status">{t(PATENT_STATUS_LABEL_KEY[record.patentStatus])}</span>
                        )}
                      </p>
                      {record.inventors && (
                        <p className="patent-row__inventors">
                          <span className="patent-row__label">{t('achievements.inventors')}</span>
                          {record.inventors}
                        </p>
                      )}
                      {certificate && (
                        <button
                          type="button"
                          className="patent-row__certificate"
                          onClick={() => setPreview({ title: pick(record.title), certificate })}
                        >
                          {t('achievements.viewCertificate')}
                        </button>
                      )}
                    </RowItem>
                  )
                })}
              </RowList>
            </section>
          )}

          {awardItems.length > 0 && (
            <section className="achievements-section">
              <SectionHead
                title={t('achievements.awards')}
                aside={<span className="achievements-section__count">{t('common.count', { n: awardItems.length })}</span>}
              />
              {/* No `min`: the card width is a design decision the theme makes
                  on .award-grid.
                  不传 min：卡片宽度是设计决策，由主题在 .award-grid 上决定。 */}
              <Grid className="award-grid">
                {awardItems.map((record) => {
                  const certificate = firstCertificate(record)
                  // The card shows a thumbnail; the dialog this card opens still
                  // loads the full PNG, so nothing a visitor chooses to look at
                  // closely is served at reduced size.
                  // 卡片用缩略图；点开这张卡片的弹窗仍然加载完整 PNG，
                  // 因此访客主动要细看的东西，没有一样是降质供给的。
                  const thumbnail = certificate ? thumbUrl(certificate.png) : ''
                  const title = pick(record.title)

                  return (
                    <Card className="award-card" key={record.id}>
                      {thumbnail && (
                        <button
                          type="button"
                          className="award-card__certificate"
                          onClick={() => setPreview({ title, certificate })}
                          aria-label={t('achievements.viewCertificate')}
                        >
                          <Media className="award-card__thumb">
                            <img src={thumbnail} alt={t('achievements.certificate')} loading="lazy" decoding="async" />
                          </Media>
                        </button>
                      )}
                      <h3 className="award-card__title">{title}</h3>
                      {pick(record.competition) && (
                        <p className="award-card__competition">{pick(record.competition)}</p>
                      )}
                      <p className="award-card__meta">
                        {pick(record.rank) && <span className="award-card__rank">{pick(record.rank)}</span>}
                        {LEVEL_LABEL_KEY[record.level] && (
                          <span className="award-card__level">{t(LEVEL_LABEL_KEY[record.level])}</span>
                        )}
                        {record.date && (
                          <time className="award-card__date" dateTime={String(record.date).slice(0, 10)}>
                            {formatDate(record.date, lang)}
                          </time>
                        )}
                      </p>
                      {record.recipients && (
                        <p className="award-card__recipients">
                          <span className="award-card__label">{t('achievements.recipients')}</span>
                          {record.recipients}
                        </p>
                      )}
                      {record.advisor && (
                        <p className="award-card__advisor">
                          <span className="award-card__label">{t('achievements.advisor')}</span>
                          {record.advisor}
                        </p>
                      )}
                    </Card>
                  )
                })}
              </Grid>
            </section>
          )}
        </PageState>
      </Container>

      <Modal
        open={Boolean(preview)}
        onClose={closePreview}
        title={preview ? preview.title : ''}
        size="lg"
        closeLabel={t('common.close')}
      >
        {preview && (
          <div className="certificate-preview">
            {/* A certificate whose PDF never converted has no PNG. The dialog
                then holds only the download link — an empty image frame would
                claim there is something to look at.
                PDF 未转换成功的证书没有 PNG，此时弹窗里只有下载链接——留一个空图框
                等于宣称「这里有东西可看」。 */}
            {assetUrl(preview.certificate.png) && (
              <img
                className="certificate-preview__image"
                src={assetUrl(preview.certificate.png)}
                alt={t('achievements.certificate')}
              />
            )}
            {assetUrl(preview.certificate.original) && (
              <a
                className="certificate-preview__download"
                href={assetUrl(preview.certificate.original)}
                download={assetName(preview.certificate.original)}
              >
                {t('achievements.downloadOriginal')}
              </a>
            )}
          </div>
        )}
      </Modal>
    </Section>
  )
}
